import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Users2, Layers3, Check } from "lucide-react";

const SCOPE_LABEL = { last_24h: "24 h", last_7d: "7 days", all_time: "All time" };

const OPTIONS = (scope) => [
  { key: "rails", icon: Layers3, title: `Rail Split · ${SCOPE_LABEL[scope]}`,
    sub: "Wallet · UPI · IMPS · NEFT · RTGS volume & share" },
  { key: "top_payers", icon: Users2, title: "Top Payers · 30 days",
    sub: "Ranked leaderboard with tx count & volume" },
  { key: "all", icon: FileSpreadsheet, title: "Full Sovereign Report",
    sub: "Rails + leaderboard + headline metrics" },
];

export function SovereignExport({ api, scope }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const download = async (section) => {
    setBusy(section);
    try {
      const r = await api.get(`/admin/vcb/analytics/export.csv?section=${section}&scope=${scope}`,
                              { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const name = /filename="?([^"]+)"?/.exec(cd)?.[1] || `vcb-sovereign-${section}.csv`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setDone(section); setTimeout(() => setDone(null), 1800);
      toast.success("Sovereign report exported", { description: name });
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="vcb-export-csv-btn"
              onClick={() => setOpen((o) => !o)}
              className="group inline-flex items-center gap-2 border border-[#D8B23E]/50 bg-[#D8B23E]/[0.06] hover:bg-[#D8B23E]/[0.14] text-[#D8B23E] px-3.5 py-1.5 rounded-sm font-grotesk text-[9px] uppercase tracking-[0.24em] transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_22px_-6px_rgba(216,178,62,0.8)]">
        <Download size={11} className="transition-transform duration-300 group-hover:translate-y-[1px]" />
        Download CSV
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.18 }}
                      data-testid="vcb-export-menu"
                      className="absolute right-0 mt-2 w-80 z-30 rounded-sm border border-[#D8B23E]/40 bg-[#0A0906]/95 backdrop-blur-xl shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] overflow-hidden">
            <div className="px-4 pt-3 pb-2 font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#8A8A82] border-b border-white/5">
              Finance Export · UTF-8 CSV
            </div>
            {OPTIONS(scope).map(({ key, icon: Icon, title, sub }, i) => (
              <motion.button key={key}
                             initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                             transition={{ delay: 0.05 * i }}
                             data-testid={`vcb-export-${key}`}
                             onClick={() => download(key)} disabled={!!busy}
                             className="w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-[#D8B23E]/[0.08] transition-colors duration-200 disabled:opacity-60">
                <span className="mt-0.5 w-7 h-7 rounded-sm border border-[#D8B23E]/30 bg-black/40 inline-flex items-center justify-center text-[#D8B23E] shrink-0">
                  {busy === key ? <Loader2 size={12} className="animate-spin" />
                    : done === key ? <Check size={12} className="text-[#2FB999]" />
                    : <Icon size={12} />}
                </span>
                <span className="min-w-0">
                  <span className="block font-serif text-[15px] text-[#F5F5F0] leading-tight">{title}</span>
                  <span className="block text-[10px] text-[#8A8A82] font-body italic mt-0.5">{sub}</span>
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
